import { Check, ChevronsUpDown } from "lucide-react"
import { useEffect, useState } from "react"

import PlatformIcon from "@/components/icons/platform-icon"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"
import { quotableTakesPath } from "@/routes"
import type { Platform, QuotableTake } from "@/types"

interface Props {
  takeKey: number
  value?: number | null
  selectedPlatforms: Platform[]
  excludeStoryId?: number
  initialSelectedTake?: QuotableTake | null
  onChange: (quotedTakeId: number | null) => void
}

function takeOptionLabel(take: QuotableTake) {
  const heading = `Take ${take.position + 1}`
  return take.label ? `${heading}: ${take.label}` : heading
}

function platformLabels(platforms: Platform[]) {
  return platforms.map((platform) => platform.label).join(", ")
}

export default function QuotedTakePicker({
  takeKey,
  value,
  selectedPlatforms,
  excludeStoryId,
  initialSelectedTake,
  onChange,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<QuotableTake[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedTake, setSelectedTake] = useState<QuotableTake | null>(
    initialSelectedTake ?? null,
  )

  // Stream matches from the backend as the user types — we never preload the whole
  // library. Debounced, and aborted when the query changes or the popover closes.
  useEffect(() => {
    if (!open) return

    // Flag loading up front — before the debounce — so the popover shows the
    // "Searching..." state instead of briefly flashing the empty state while we
    // wait to fetch.
    setLoading(true)

    const controller = new AbortController()
    const handle = window.setTimeout(() => {
      void (async () => {
        try {
          const params = new URLSearchParams()
          if (query) params.set("q", query)
          if (excludeStoryId != null) {
            params.set("exclude_story_id", String(excludeStoryId))
          }
          const response = await fetch(`${quotableTakesPath()}?${params}`, {
            headers: { Accept: "application/json" },
            signal: controller.signal,
          })
          if (response.ok) {
            setResults((await response.json()) as QuotableTake[])
          }
        } catch {
          // Ignore aborts and transient network errors.
        } finally {
          setLoading(false)
        }
      })()
    }, 200)

    return () => {
      controller.abort()
      window.clearTimeout(handle)
    }
  }, [open, query, excludeStoryId])

  // Only platforms that can carry a link matter here. The quoted take quotes/links
  // where it's already published, and blocks publishing where it isn't.
  const quotablePlatforms = selectedPlatforms.filter(
    (platform) => platform.quoted_take_style != null,
  )
  const publishedOn = (platform: Platform) =>
    selectedTake?.published_platforms.some(
      (published) => published.id === platform.id,
    ) ?? false
  const quotedOn = selectedTake
    ? quotablePlatforms.filter(
        (platform) =>
          platform.quoted_take_style === "quote" && publishedOn(platform),
      )
    : []
  const linkedOn = selectedTake
    ? quotablePlatforms.filter(
        (platform) =>
          platform.quoted_take_style === "link" && publishedOn(platform),
      )
    : []
  const blockedOn = selectedTake
    ? quotablePlatforms.filter((platform) => !publishedOn(platform))
    : []

  function choose(take: QuotableTake) {
    setSelectedTake(take)
    onChange(take.id)
    setOpen(false)
  }

  function clear() {
    setSelectedTake(null)
    onChange(null)
    setOpen(false)
  }

  const triggerLabel = selectedTake
    ? takeOptionLabel(selectedTake)
    : value != null
      ? "Quoted take"
      : "Choose a take to quote"

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={`take-link-${takeKey}`}>Take to quote</Label>
      <Popover
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setQuery("")
        }}
      >
        <PopoverTrigger asChild>
          <Button
            id={`take-link-${takeKey}`}
            type="button"
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between font-normal"
          >
            <span className="truncate">{triggerLabel}</span>
            <ChevronsUpDown className="ml-2 size-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-(--radix-popover-trigger-width) p-0"
          align="start"
        >
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search published takes..."
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              {loading && (
                <div className="text-muted-foreground py-6 text-center text-sm">
                  Searching...
                </div>
              )}
              {!loading && results.length === 0 && (
                <div className="text-muted-foreground py-6 text-center text-sm">
                  No published takes found.
                </div>
              )}
              <CommandGroup>
                {value != null && (
                  <CommandItem value="__clear__" onSelect={clear}>
                    <Check className="mr-2 size-4 opacity-0" />
                    Don&apos;t quote a take
                  </CommandItem>
                )}
                {results.map((take) => (
                  <CommandItem
                    key={take.id}
                    value={String(take.id)}
                    onSelect={() => choose(take)}
                  >
                    <Check
                      className={cn(
                        "mr-2 size-4 shrink-0",
                        value === take.id ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <span className="truncate">{takeOptionLabel(take)}</span>
                    {take.published_platforms.length > 0 && (
                      <span className="ml-auto flex shrink-0 items-center gap-1">
                        {take.published_platforms.map((published) => (
                          <PlatformIcon
                            key={published.id}
                            platform={published.name}
                            className="size-3.5"
                          />
                        ))}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {selectedTake && (quotedOn.length > 0 || linkedOn.length > 0) && (
        <p className="text-muted-foreground text-sm">
          {quotedOn.length > 0 && <>Quotes on {platformLabels(quotedOn)}. </>}
          {linkedOn.length > 0 && <>Links on {platformLabels(linkedOn)}.</>}
        </p>
      )}
      {blockedOn.length > 0 && (
        <p className="text-destructive text-sm">
          That take isn&apos;t published on {platformLabels(blockedOn)} yet —
          publishing will be blocked until it is.
        </p>
      )}
    </div>
  )
}
